import React from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { getRechartsTheme } from '../utils/chartTheme';
import { AllVersionsLineKey } from '../hooks/useDashboardChartVisibility';
import { DASHBOARD_TOP_VERSION_COUNT, VersionStatRow } from '../utils/versionChartUtils';
import { TumVersiyonlarLine } from './TumVersiyonlarLine';

interface MetricLineChartCardProps {
  title: string;
  data: Record<string, unknown>[];
  versionStatsTop: VersionStatRow[];
  allVersionsTotalCount: number;
  hiddenVersions: Set<string>;
  setHiddenVersions: React.Dispatch<React.SetStateAction<Set<string>>>;
  allVersionsLineKey: AllVersionsLineKey;
  hiddenAllVersionsLine: Set<AllVersionsLineKey>;
  setHiddenAllVersionsLine: React.Dispatch<React.SetStateAction<Set<AllVersionsLineKey>>>;
}

export const MetricLineChartCard: React.FC<MetricLineChartCardProps> = ({
  title,
  data,
  versionStatsTop,
  allVersionsTotalCount,
  hiddenVersions,
  setHiddenVersions,
  allVersionsLineKey,
  hiddenAllVersionsLine,
  setHiddenAllVersionsLine,
}) => {
  const theme = getRechartsTheme();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 dark:border-gray-700">
      <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">{title}</h3>
      {data.length === 0 ? (
        <div className="h-[300px] flex items-center justify-center rounded-xl border border-dashed border-gray-200 dark:border-gray-700 text-slate-500 dark:text-slate-400 font-medium">
          Loading...
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.gridStroke} />
            <XAxis
              dataKey="name"
              stroke={theme.axisStroke}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis domain={[0, 'dataMax + 10']} stroke={theme.axisStroke} />
            <Legend
              wrapperStyle={{
                color: theme.legendColor,
                cursor: 'pointer',
              }}
              onClick={(e: { dataKey?: unknown }) => {
                const dataKey = e.dataKey != null ? String(e.dataKey) : undefined;
                if (dataKey === 'Tüm Versiyonlar') {
                  setHiddenAllVersionsLine(prev => {
                    const next = new Set(prev);
                    if (next.has(allVersionsLineKey)) next.delete(allVersionsLineKey);
                    else next.add(allVersionsLineKey);
                    return next;
                  });
                } else if (dataKey) {
                  setHiddenVersions(prev => {
                    const next = new Set(prev);
                    if (next.has(dataKey)) next.delete(dataKey);
                    else next.add(dataKey);
                    return next;
                  });
                }
              }}
              iconType="line"
              formatter={(value: string) =>
                value === 'Tüm Versiyonlar' ? 'Tüm Versiyonlar' : `Versiyon ${value}`
              }
            />
            {versionStatsTop.map((stat, index) => {
              const version = stat.version;
              const hue = (index * 137) % 360;
              const color = `hsl(${hue}, 70%, 50%)`;
              const isHidden = hiddenVersions.has(version);
              return (
                <Line
                  key={version}
                  type="monotone"
                  dataKey={version}
                  stroke={color}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  name={version}
                  hide={isHidden}
                  strokeOpacity={isHidden ? 0 : 1}
                />
              );
            })}
            {allVersionsTotalCount > DASHBOARD_TOP_VERSION_COUNT && (
              <TumVersiyonlarLine hidden={hiddenAllVersionsLine.has(allVersionsLineKey)} />
            )}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
